<!-- eslint-disable vue/no-v-html -->
<template>
    <header
        ref="header"
        class="dhf-header"
        :class="{
            'dhf-header--persist': persistent && !minimal,
            'dhf-header--open': mobileNavOpen,
            'dhf-header--blurry': headerIsBlurry,
            'dhf-header--collapsed': headerIsCollapsed,
            'dhf-header--overlay-open': isOverlayOpen,
        }"
        @mouseenter="headerMouseEnterHandler"
        @mouseleave="headerMouseLeaveHandler"
        @keyup.esc="onPressEscape"
    >
        <dhf-notification-banner
            v-if="isNotificationVisible"
            :title="notificationData.title"
            :message="notificationData.copy"
            :link-text="notificationData?.link?.label"
            :link-href="notificationData?.link?.href"
            @on-dismiss="onDismissNotification"
        />
        <div class="dhf-header__contents">
            <div class="dhf-header__actions">
                <a
                    class="dhf-header__actions-logo"
                    :href="logoLink"
                    aria-label="logo"
                    data-testId="dhf-header-logo"
                    @click="
                        doHeaderDataLayerPush(
                            false,
                            EventActionsHeader.logo,
                            'Miele Logo',
                            logoLink,
                            'Miele Logo',
                            NavigationLevel.primary
                        )
                    "
                >
                    <DhfLogo />
                </a>

                <div v-if="navData && !disableNavIcons" class="dhf-header__actions-icons">
                    <dhf-language-select
                        v-if="resolvedLanguageResources"
                        :language-resources="resolvedLanguageResources"
                        :custom-class-list="'dhf-header__actions-item dhf-header__actions-item--language'"
                    />

                    <span
                        v-for="icon in navData.subMenu"
                        :key="icon.type"
                        :class="`dhf-header__actions-item dhf-header__actions-item--${icon.type}`"
                    >
                        <button
                            v-if="isMenuItemOverlay(icon.type)"
                            :aria-label="icon.label"
                            :data-testId="`dhf-header-actions-${icon.type}`"
                            class="dhf-header__actions-item-button"
                            @click="
                                handleActionMenuClick(icon.type);
                                doHeaderDataLayerPush(true, icon.label, '', NavigationLevel.primary, '', '');
                            "
                        >
                            <span class="dhf-header__nav-icon">
                                <DhfIcon :name="icon.type" color="white" :width="24" :height="24" />
                            </span>
                            <span class="dhf-header__nav-icon--inverted">
                                <DhfIcon :name="`${icon.type}-inverted`" color="white" :width="24" :height="24" />
                            </span>
                            <span
                                v-if="(icon.type === 'cart' || icon.type === 'bag') && itemsCountInCart >= 1"
                                class="dhf-header__actions-item-badge"
                            >
                                {{ itemsCountInCart }}
                            </span>
                            <span class="sr-only" v-html="icon.label"></span>
                        </button>
                        <a
                            v-else
                            :href="icon.href"
                            :aria-label="icon.label"
                            :data-testId="`dhf-header-actions-${icon.type}`"
                            class="dhf-header__actions-item-link"
                            @click="doHeaderDataLayerPush(true, icon.label, '', NavigationLevel.primary, '', '')"
                        >
                            <span class="dhf-header__nav-icon">
                                <DhfIcon :name="icon.type" color="white" :width="24" :height="24" />
                            </span>
                            <span class="dhf-header__nav-icon--inverted">
                                <DhfIcon :name="`${icon.type}-inverted`" color="white" :width="24" :height="24" />
                            </span>
                            <span class="sr-only" v-html="icon.label"></span>
                        </a>
                    </span>
                </div>
            </div>

            <dhf-nav
                v-if="!minimal"
                ref="headerNav"
                :menu-data="navData?.menu"
                :language-object="props?.languageObject"
                :sub-menu-data="navData?.subMenu"
                @toggle-mobile-nav="toggleMobileNav"
                @handle-action-menu-click="handleActionMenuClick"
            />
        </div>

        <DhfOverlay v-if="isOverlayOpen" @close-modal="closeOverlay">
            <DhfCart
                v-if="overlayContent == 'bag'"
                :carts="cartContents"
                :cart-status="cartStatus"
                :total-cart-items="itemsCountInCart"
                :logged-in="accountAuthenticated"
            />
            <DhfAccount
                v-if="overlayContent == 'accounts'"
                :is-authenticated="accountAuthenticated"
                :has-logged-out="hasLoggedOut"
                :customer-data="customerData"
                :sign-out-action="signOutAction"
            />
        </DhfOverlay>
    </header>
</template>
<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, defineAsyncComponent, type Ref } from 'vue';
import DhfIcon from '@/components/dhf-icon/dhf-icon.vue';
import DhfLogo from '@/components/dhf-logo/dhf-logo.vue';
import DhfOverlay from '@/components/dhf-overlay/dhf-overlay.vue';
import { useTracking } from '@/composables/useTracking';
import useNotificationState from '@/composables/useNotificationState';
import { compressNavJson } from '@/utils/navUtils';
import type {
    HeaderProps,
    Icon,
    NotificationBannerProps,
    ILanguageResources,
    ILanguageObject,
} from '@/types/shared-types';
import type { Cart } from '@/types/cart-types';
import type { Customer } from '@/types/customer-types';
import type { Status } from '@/constants/api.constants';
import type { SignOutAction } from '@/composables/useAccount';
import DhfLanguageSelect from '@/components/dhf-language-select/dhf-language-select.vue';
import { useAlternateLanguage } from '@/composables/useAlternateLanguage';
import { getDomainLanguage } from '@/utils/domainUtils';

const DhfNav = defineAsyncComponent(() => import('@/components/dhf-nav/dhf-nav.vue'));
const DhfCart = defineAsyncComponent(() => import('@/components/dhf-cart/dhf-cart.vue'));
const DhfAccount = defineAsyncComponent(() => import('@/components/dhf-account/dhf-account.vue'));
const DhfNotificationBanner = defineAsyncComponent(
    () => import('@/components/dhf-notification-banner/dhf-notification-banner.vue')
);

const props = defineProps<{
    data?: HeaderProps | null;
    cartContents: Cart[] | null;
    cartCount?: number;
    cartStatus: Status;
    accountAuthenticated: boolean;
    hasLoggedOut: boolean;
    customerData: Customer | null;
    signOutAction: SignOutAction;
    persistent: boolean;
    minimal: boolean;
    disableNavIcons: boolean;
    languageObject?: ILanguageObject | undefined;
}>();

const { EventActionsHeader, NavigationLevel, doHeaderDataLayerPush } = useTracking();

const navData = ref<HeaderProps>();
const bodyEl = ref<HTMLElement>();
const header = ref<HTMLElement>();
const logoLink = ref<string>('/');
const isOverlayOpen = ref<boolean>(false);
const overlayContent = ref<Icon['type'] | null>();
const lastScrollTop = ref<number>(0);
const headerIsBlurry = ref<boolean>(false);
const headerIsCollapsed = ref<boolean>(false);
const headerNav = ref<InstanceType<typeof DhfNav> | null>(null);
const mobileNavOpen = ref<boolean>(false);

const resolvedLanguageResources = ref<ILanguageResources>();

const emit = defineEmits(['handleMiniCartClick']);

let isNotificationVisible = ref<boolean>(false);
let notificationData: Ref<NotificationBannerProps>;
let onDismissNotification: () => boolean;

// Compress Nav Json to remove extra nested levels from nav tree
// TODO: remove this calculation when navigation json json is updated
if (props.data) navData.value = compressNavJson(props.data);
watch(
    () => props.data,
    () => {
        if (props.data) navData.value = compressNavJson(props.data);
    }
);

const hideOverflowClass = 'dhf-overflow-hidden';
const isMenuItemOverlay = (menuItem: Icon['type']) => menuItem === 'accounts' || menuItem === 'bag';

const itemsCountInCart = computed(() => {
    const carts = props.cartContents;
    if (!carts?.length) return props.cartCount || 0;

    const cartItemCount = carts?.reduce((acc: number, curr: Cart) => {
        return acc + (curr.totalUnitCount ?? curr.totalItems ?? curr.entries?.length ?? 0);
    }, 0);
    return cartItemCount ?? 0;
});

const handleActionMenuClick = (menuItem: Icon['type']) => {
    if (menuItem === 'bag' || menuItem === 'cart') emit('handleMiniCartClick');
    if (isOverlayOpen.value) {
        if (overlayContent.value === menuItem) {
            isOverlayOpen.value = false;
        }
    } else {
        isOverlayOpen.value = true;
        bodyEl.value?.classList.add(hideOverflowClass);
        if (mobileNavOpen.value) toggleMobileNav(false);
    }
    overlayContent.value = menuItem;
};

const closeOverlay = () => {
    overlayContent.value = null;
    isOverlayOpen.value = false;
    bodyEl.value?.classList.remove(hideOverflowClass);
};

const onPressEscape = () => {
    closeOverlay();
    headerNav.value?.closeAllNavs();
    if (mobileNavOpen.value) toggleMobileNav(false);
};

function toggleMobileNav(isOpen: boolean) {
    mobileNavOpen.value = isOpen;
    if (isOpen) {
        bodyEl.value?.classList.add(hideOverflowClass);
    } else {
        bodyEl.value?.classList.remove(hideOverflowClass);
    }
}

function windowScrollHandler() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop <= 0) {
        headerIsCollapsed.value = false;
        headerIsBlurry.value = false;
    } else if (scrollTop > lastScrollTop.value) {
        headerIsBlurry.value = true;
        headerIsCollapsed.value = true;
        closeOverlay();
    } else {
        headerIsCollapsed.value = false;
    }
    lastScrollTop.value = scrollTop;
}

function windowResizeHandler() {
    headerNav.value?.closeAllNavs();
}

function headerMouseEnterHandler() {
    headerIsCollapsed.value = false;
    headerIsBlurry.value = false;
}

function headerMouseLeaveHandler() {
    if (lastScrollTop.value > 0) {
        headerIsBlurry.value = true;
        headerIsCollapsed.value = true;
    }
}

onMounted(async () => {
    logoLink.value = getDomainLanguage(window.location.href)
        ? `${window.location.origin}/${getDomainLanguage(window.location.href)}`
        : window.location.origin;
    bodyEl.value = document.getElementsByTagName('body')[0];
    window.addEventListener('resize', windowResizeHandler);
    if (props.persistent && !props.minimal) {
        window.addEventListener('scroll', windowScrollHandler);
    }

    ({ isNotificationVisible, notificationData, onDismissNotification } = useNotificationState(navData));

    const languageSelect = useAlternateLanguage() ?? undefined;

    if (languageSelect) {
        if (!props.languageObject) {
            const { data } = languageSelect.useLanguageQuery();

            if (data.value) {
                resolvedLanguageResources.value = await languageSelect.getLanguageResources(data.value);
            }

            watch(data, async () => {
                try {
                    resolvedLanguageResources.value = await languageSelect.getLanguageResources(data.value);
                } catch (err) {
                    console.error(err);
                    throw err;
                }
            });
        } else if (props.languageObject && props.languageObject.languages?.length > 1) {
            resolvedLanguageResources.value = (await languageSelect.getLanguageResources(props.languageObject)) ?? null;

            watch([props], async () => {
                try {
                    resolvedLanguageResources.value =
                        (await languageSelect.getLanguageResources(props.languageObject)) ?? null;
                } catch (err) {
                    console.error(err);
                    throw err;
                }
            });
        }
    }
});

onUnmounted(() => {
    window.removeEventListener('resize', windowResizeHandler);
    if (props.persistent && !props.minimal) {
        window.removeEventListener('scroll', windowScrollHandler);
    }
});
</script>

<style lang="scss">
@import './dhf-header.scss';
</style>
